import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../utils/errorLogging';
import modalStyles from '../../styles/modals.module.css';

// Map cost types to their display units
const unitMap = {
  'מחיר קבוע': 'מחיר קבוע',
  'מחיר למטר': 'למטר',
  'מחיר למטר רבוע': 'למטר רבוע',
  'מחיר ליחידה': 'ליחידה',
  'מחיר חודשי': 'לחודש',
};

/**
 * Modal for adding/editing custom BOQ items.
 * Creates a project_item with master_item_id=null and bb_chapter_id set.
 * Uses direct Supabase insert (not the manage_project_items RPC, which requires a master_item).
 */
const BOQFormModal = ({
  open,
  onClose,
  onSubmitRef,
  bbChapters = [],
  costTypes = [],
  projectId,
  item = null,
  onSuccess,
}) => {
  const { logError } = useErrorLogging();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    bb_chapter_id: item?.bb_chapter_id || '',
    name_override: item?.name_override || '',
    notes: item?.details?.notes || '',
    cost_type_id: item?.master_items?.cost_type_id || item?.details?.cost_type_id || '',
    quantity: item?.quantity || '',
  });
  const [errors, setErrors] = useState({});
  const isEditMode = !!item;

  // Only show top-level chapters (depth=0) in the dropdown for simplicity
  const topLevelChapters = bbChapters
    ?.filter(ch => ch.depth === 0)
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        bb_chapter_id: item?.bb_chapter_id || '',
        name_override: item?.name_override || '',
        notes: item?.details?.notes || '',
        cost_type_id: item?.master_items?.cost_type_id || item?.details?.cost_type_id || '',
        quantity: item?.quantity || '',
      });
      setErrors({});
    }
  }, [open, item]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name_override.trim()) {
      newErrors.name_override = 'נדרש שם פריט';
    }
    if (!formData.bb_chapter_id) {
      newErrors.bb_chapter_id = 'נדרש פרק';
    }
    if (formData.quantity && Number(formData.quantity) <= 0) {
      newErrors.quantity = 'כמות חייבת להיות חיובית';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const itemData = {
        project_id: projectId,
        master_item_id: null,
        bb_chapter_id: formData.bb_chapter_id,
        name_override: formData.name_override.trim(),
        details: {
          ...(formData.notes ? { notes: formData.notes } : {}),
          ...(formData.cost_type_id ? { cost_type_id: formData.cost_type_id } : {}),
        },
        quantity: Number(formData.quantity) || 1,
        status: 'planned',
      };

      if (isEditMode && item?.id) {
        // Update existing custom item
        const { error } = await supabase
          .from('project_items')
          .update({
            bb_chapter_id: itemData.bb_chapter_id,
            name_override: itemData.name_override,
            details: itemData.details,
            quantity: itemData.quantity,
          })
          .eq('id', item.id);
        if (error) throw error;
      } else {
        // Insert new custom item
        const { error } = await supabase.from('project_items').insert(itemData);
        if (error) throw error;
      }

      // Notify parent to refetch
      onSuccess?.();
      onClose();
    } catch (error) {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to save custom BOQ item',
        metadata: { formData },
      });
      setErrors(prev => ({
        ...prev,
        submit: 'שגיאה בשמירת הפריט. אנא נסה שוב.',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Register submit handler with ModalManager
  useEffect(() => {
    if (open && onSubmitRef) {
      const handleKeyDown = e => {
        if (e.key === 'Enter') {
          handleSubmit();
        }
      };
      onSubmitRef(handleKeyDown);
      return () => {
        onSubmitRef(null);
      };
    }
  }, [open, onSubmitRef, handleSubmit]);

  if (!open) return null;

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modal} dir="rtl" onClick={e => e.stopPropagation()}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.headerTitle}>
            {isEditMode ? 'ערוך פריט' : 'הוסף פריט מותאם אישית'}
          </h2>
        </div>
        <div className={modalStyles.content}>
          {errors.submit && <div className={modalStyles.error}>{errors.submit}</div>}
          <div className={modalStyles.formGrid}>
            {/* Item name — required */}
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                <span className={modalStyles.fieldRequired}>*</span>
                שם פריט
              </label>
              <input
                type="text"
                className={modalStyles.fieldInput}
                name="name_override"
                value={formData.name_override}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="לדוגמה: התקנת דלת פנים"
              />
              {errors.name_override && (
                <div className={modalStyles.fieldError}>{errors.name_override}</div>
              )}
            </div>
            {/* BB chapter — required */}
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                <span className={modalStyles.fieldRequired}>*</span>
                פרק
              </label>
              <select
                className={modalStyles.fieldSelect}
                name="bb_chapter_id"
                value={formData.bb_chapter_id}
                onChange={handleChange}
                disabled={isLoading}
              >
                <option value="">בחר פרק...</option>
                {topLevelChapters?.map(ch => (
                  <option key={ch.id} value={ch.id}>
                    {ch.code} - {ch.name_he}
                  </option>
                ))}
              </select>
              {errors.bb_chapter_id && (
                <div className={modalStyles.fieldError}>{errors.bb_chapter_id}</div>
              )}
            </div>
            {/* Description / notes — optional */}
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                תיאור
                <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
              </label>
              <input
                type="text"
                className={modalStyles.fieldInput}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
            {/* Quantity and unit row */}
            <div className={modalStyles.formRow}>
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>
                  כמות
                  <span className={modalStyles.fieldOptional}>(ברירת מחדל: 1)</span>
                </label>
                <input
                  type="number"
                  className={modalStyles.fieldInput}
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  disabled={isLoading}
                  min="0"
                  step="0.01"
                  placeholder="1"
                />
                {errors.quantity && <div className={modalStyles.fieldError}>{errors.quantity}</div>}
              </div>
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>
                  יחידת מדידה
                  <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
                </label>
                <select
                  className={modalStyles.fieldSelect}
                  name="cost_type_id"
                  value={formData.cost_type_id}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  <option value="">-</option>
                  {costTypes
                    ?.sort((a, b) => a.name.localeCompare(b.name))
                    .map(type => (
                      <option key={type.id} value={type.id}>
                        {unitMap[type.name] || type.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className={modalStyles.footer}>
          <button
            className={modalStyles.cancelButton}
            onClick={onClose}
            disabled={isLoading}
            type="button"
          >
            ביטול
          </button>
          <button
            className={modalStyles.primaryButton}
            onClick={handleSubmit}
            disabled={isLoading}
            type="submit"
          >
            {isLoading ? 'שומר...' : isEditMode ? 'עדכן' : 'שמור'}
          </button>
        </div>
      </div>
    </div>
  );
};
export default BOQFormModal;
